import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  RESUMO_FINANCEIRO_LIGA,
  SERIE_POR_DIA_LIGA,
  QUEBRA_POR_CATEGORIA_LIGA,
  ResumoFinanceiroLigaData,
  SeriePorDiaLigaData,
  QuebraPorCategoriaLigaData,
  FiltroLigaVars,
} from '../services/financeiroService';
import type { ResumoFinanceiro, PontoSerieFinanceira, QuebraFinanceira, PeriodoFinanceiro } from '../types/financeiro';

// Espelha hooks/useFinanceiroAtleta.ts (financeiro-dashboard-atleta F-2), com `ligaId` explícito
// em vez do recorte-por-token: as 3 queries de liga exigem `ligaId: ID!` (Task 1/Task 2 do plano
// desta fase). Mesmas regras do molde do atleta:
// 1. `fetchPolicy: 'no-cache'` nas 3 — o hub nunca lê de cache stale entre trocas de período.
// 2. `resumoIndisponivel` distingue "servidor devolveu null" (contrato nullable, QA-17) de
//    "falha de rede" (`error`).
// 3. `refetch` dispara as 3 novamente — usado pela ação "Tentar novamente" das AC-A2.
// 4. Nenhuma soma de valor monetário aqui: o hook só repassa o que veio das 3 queries.

export interface UseFinanceiroLigaResult {
  periodo: PeriodoFinanceiro;
  setPeriodo: (periodo: PeriodoFinanceiro) => void;
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  quebra: QuebraFinanceira[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  refetch: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateLocal(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Primeiro e último dia do mês corrente, por aritmética de Date LOCAL (nunca UTC) — usar UTC
// deslocaria o período para quem está em UTC-3 (Risks and Mitigations do plano).
function periodoMesCorrente(): PeriodoFinanceiro {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const fim = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);
  return { inicio: formatDateLocal(inicio), fim: formatDateLocal(fim) };
}

export function useFinanceiroLiga(ligaId: string): UseFinanceiroLigaResult {
  const [periodo, setPeriodo] = useState<PeriodoFinanceiro>(periodoMesCorrente());

  const resumoQuery = useQuery<ResumoFinanceiroLigaData, FiltroLigaVars>(RESUMO_FINANCEIRO_LIGA, {
    variables: { ligaId, filtro: periodo },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const serieQuery = useQuery<SeriePorDiaLigaData, FiltroLigaVars>(SERIE_POR_DIA_LIGA, {
    variables: { ligaId, filtro: periodo },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const quebraQuery = useQuery<QuebraPorCategoriaLigaData, FiltroLigaVars>(QUEBRA_POR_CATEGORIA_LIGA, {
    variables: { ligaId, filtro: periodo },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const resumo = resumoQuery.data?.resumoFinanceiroLiga ?? null;
  const serie = serieQuery.data?.seriePorDiaLiga ?? [];
  const quebra = quebraQuery.data?.quebraPorCategoriaLiga ?? [];
  const loading = resumoQuery.loading || serieQuery.loading || quebraQuery.loading;
  const error = resumoQuery.error || serieQuery.error || quebraQuery.error;
  const resumoIndisponivel = !resumoQuery.loading && !resumoQuery.error && resumo === null;

  const refetch = () => {
    resumoQuery.refetch();
    serieQuery.refetch();
    quebraQuery.refetch();
  };

  return { periodo, setPeriodo, resumo, serie, quebra, loading, error, resumoIndisponivel, refetch };
}
